import { ActionIcon, Flex, Pagination } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnFiltersState,
  MRT_ExpandedState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableOptions,
  useMantineReactTable,
} from "mantine-react-table";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

interface CommonTableProps<TData extends Record<string, any> = {}> {
  columns: MRT_TableOptions<TData>["columns"];
  data: TData[];
  totalCount?: number;
  totalPages?: number;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  sorting?: MRT_SortingState;
  setSorting?: Dispatch<SetStateAction<MRT_SortingState>>;
  columnFilters?: MRT_ColumnFiltersState;
  setColumnFilters?: Dispatch<SetStateAction<MRT_ColumnFiltersState>>;
  globalFilter?: string;
  setGlobalFilter?: Dispatch<SetStateAction<string>>;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: Dispatch<SetStateAction<Record<string, boolean>>>;
  selectedRowIds?: string[];
  setSelectedRowIds?: Dispatch<SetStateAction<string[]>>;
  enableRowSelection?: boolean;
  enableRowActions?: boolean;
  positionActionsColumn?: boolean;
  enableEditing?: boolean;
  enableTopToolbar?: boolean;
  renderRowActions?: MRT_TableOptions<TData>["renderRowActions"];
  renderDetailPanel?: MRT_TableOptions<TData>["renderDetailPanel"];
  enableColumnActions?: boolean;
  singleExpand?: boolean;
}

export const MantineReactCommonTable = <TData extends { id?: number | any }>({
  columns,
  data,
  totalCount,
  totalPages,
  pagination,
  setPagination,
  sorting = [],
  setSorting,
  columnFilters = [],
  setColumnFilters,
  globalFilter,
  setGlobalFilter,
  rowSelection = {},
  setRowSelection,
  selectedRowIds,
  setSelectedRowIds,
  enableRowSelection,
  enableRowActions,
  positionActionsColumn,
  enableEditing,
  enableTopToolbar,
  renderRowActions,
  renderDetailPanel,
  enableColumnActions,
  singleExpand = false,
  ...rest
}: CommonTableProps<TData>) => {
  useEffect(() => {
    setPagination((prevState) => ({ ...prevState, pageIndex: 0 }));
  }, [columnFilters, globalFilter, sorting]);

  const isExpandable = !!renderDetailPanel;

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const handleExpandedChange = (
    updaterOrValue: MRT_ExpandedState | ((old: MRT_ExpandedState) => MRT_ExpandedState),
  ) => {
    const next =
      typeof updaterOrValue === "function"
        ? (updaterOrValue as (old: MRT_ExpandedState) => MRT_ExpandedState)(expanded)
        : updaterOrValue;

    if (!singleExpand) {
      setExpanded(next as Record<string, boolean>);
      return;
    }

    // Remove the incorrect boolean usage
    if (next === true) {
      setExpanded({});
      return;
    }

    let lastTrueKey: string | undefined;
    const keys = Object.keys(next ?? {});
    for (let i = keys.length - 1; i >= 0; i--) {
      const k = keys[i];
      if (next && (next as Record<string, boolean>)[k]) {
        lastTrueKey = k;
        break;
      }
    }

    setExpanded(lastTrueKey ? { [lastTrueKey]: true } : {});
  };

  const getRowId = (row: TData, index: number) => row.id?.toString() ?? String(index);

  const table = useMantineReactTable<TData>({
    columns,
    data,
    editDisplayMode: "modal",
    enableEditing: enableEditing ?? false,
    enableRowSelection: enableRowSelection ?? false,
    rowCount: totalCount,
    getRowId,
    positionActionsColumn: positionActionsColumn ? "last" : undefined,
    enablePagination: true,
    enableTopToolbar: enableTopToolbar ?? false,
    manualFiltering: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    selectDisplayMode: "checkbox",
    enableSorting: true,
    enableStickyHeader: true,
    state: {
      columnFilters,
      globalFilter,
      pagination,
      expanded,
    },
    onExpandedChange: handleExpandedChange,
    enableExpanding: isExpandable,
    getIsRowExpanded: (row) => !!expanded[row.id] || false, // 실제 expanded 상태 반환
    renderDetailPanel: (props) => renderDetailPanel?.(props) ?? null,
    displayColumnDefOptions: {
      "mrt-row-expand": {
        header: "",
        size: 44,
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <ActionIcon
            variant="subtle"
            aria-label={row.getIsExpanded() ? "Collapse row" : "Expand row"}
            onClick={() => row.toggleExpanded()}
          >
            {row.getIsExpanded() ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
          </ActionIcon>
        ),
      },
    },

    ...rest,
  });

  return (
    <>
      <MantineReactTable table={table} />
      <Flex justify="center" style={{ padding: "1rem 0" }}>
        <Pagination
          onChange={(value: number) =>
            setPagination((prevState) => ({ ...prevState, pageIndex: value }))
          }
          value={pagination.pageIndex}
          total={totalPages ?? 0}
          size="lg"
          radius="sm"
        />
      </Flex>
    </>
  );
};
